import type { CompaniaArbol } from "../../types/accesosOrganizacion.types";

interface ResumenAccesosProps {
    arbol: CompaniaArbol[];
    borrador: Set<number>;
    marcar: (codigos: number[], valor: boolean) => void;
    principal: number | null;
    setPrincipal: (codigo: number) => void;
}

// Ramas del árbol con al menos una sede marcada.
const ramasMarcadas = (arbol: CompaniaArbol[], borrador: Set<number>) =>
    arbol
        .map((c) => ({
            compania: c,
            sucursales: c.sucursales
                .map((s) => ({ sucursal: s, sedes: s.sedes.filter((x) => borrador.has(x.codigo)) }))
                .filter((s) => s.sedes.length > 0),
        }))
        .filter((c) => c.sucursales.length > 0)

// Panel lateral del modal: totales y sedes marcadas por compañía/sucursal.
export const ResumenAccesos = ({ arbol, borrador, marcar, principal, setPrincipal }: ResumenAccesosProps) => {
    const ramas = ramasMarcadas(arbol, borrador)
    const totales = [
        { n: ramas.length, txt: "Compañías" },
        { n: ramas.reduce((a, c) => a + c.sucursales.length, 0), txt: "Sucursales" },
        { n: borrador.size, txt: "Sedes" },
    ]
    return (
        <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 lg:tw-sticky lg:tw-top-0">
            <div className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Resumen de accesos</div>
            <div className="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-4">
                {totales.map((t) => (
                    <div key={t.txt} className="tw-rounded-lg tw-bg-indigo-50 tw-px-2 tw-py-2 tw-text-center">
                        <div className="tw-text-xl tw-font-bold tw-text-primario tw-leading-none">{t.n}</div>
                        <div className="tw-text-[11px] tw-text-gray-600 tw-mt-1">{t.txt}</div>
                    </div>
                ))}
            </div>
            {ramas.length === 0 && (
                <p className="tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-p-4 tw-text-center tw-text-xs tw-text-gray-500 tw-mb-0">
                    Aún no se marcó ninguna sede. Use las casillas del árbol o "Todas" en una compañía o sucursal.
                </p>
            )}
            {ramas.length > 0 && <p className="tw-text-[11px] tw-text-gray-500 tw-mb-2">Clic en una sede para hacerla la sede principal del usuario.</p>}
            <div className="tw-flex tw-flex-col tw-gap-3 tw-max-h-[40vh] tw-overflow-y-auto">
                {ramas.map(({ compania, sucursales }) => (
                    <div key={compania.codigo}>
                        <div className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-gray-700">{compania.nombre}</div>
                        {sucursales.map(({ sucursal, sedes }) => (
                            <div key={sucursal.codigo} className="tw-mt-1.5 tw-border-l-2 tw-border-indigo-100 tw-pl-2">
                                <div className="tw-text-xs tw-text-gray-500">{sucursal.nombre} · {sedes.length} de {sucursal.sedes.length}</div>
                                <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1">
                                    {sedes.map((s) => (
                                        <span key={s.codigo} className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-pl-2 tw-pr-1 tw-py-0.5 tw-text-xs ${s.codigo === principal ? "tw-border-primario tw-bg-primario tw-text-white" : "tw-border-gray-200 tw-bg-gray-50 tw-text-gray-700"}`}>
                                            <button type="button" title="Marcar como sede principal" onClick={() => setPrincipal(s.codigo)}
                                                className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-current">{s.nombre}</button>
                                            {s.codigo === principal && <span className="tw-text-[10px] tw-opacity-80">· principal</span>}
                                            <button type="button" aria-label={`Quitar ${s.nombre}`} onClick={() => marcar([s.codigo], false)}
                                                className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-current hover:tw-bg-black/10">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
